import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from 'hooks/useAuth';
import { authService } from 'services/authService';
import { tokenManager } from 'util/tokenManager';

/**
 * Broadcast that the tenant's own record changed (Center Profile / Organization
 * Profile saving a new name or logo).
 *
 * Every `useTenant()` call owns its own copy of the tenant, so the profile
 * screen calling its own `reload()` would refresh nothing but itself — the
 * sidebar that actually displays the name and logo holds a different instance.
 * A window event is what reaches all of them without threading a provider
 * through the tree for one value.
 */
export const TENANT_UPDATED_EVENT = 'anablr:tenant-updated';

export const notifyTenantUpdated = () => {
  window.dispatchEvent(new Event(TENANT_UPDATED_EVENT));
};

/**
 * The center or organization the current session is scoped to
 * (`GET /auth/tenant` → `{ scope, id, name, logo_url, logo_shape }`).
 *
 * Re-fetched whenever the signed-in identity or its center/organization scope
 * changes, so switching center can never leave the previous tenant on screen
 * (that path also reloads the page today — this does not depend on it).
 * Failures resolve to `null` so the badge disappears instead of breaking the
 * rail; a demo/local session never calls the API at all.
 */
export function useTenant() {
  const { isAuthenticated, user } = useAuth();
  const scopeKey = `${user?.id ?? ''}:${user?.centerId ?? ''}:${user?.organizationId ?? ''}`;
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(Boolean(isAuthenticated));
  const requestRef = useRef(0);

  const load = useCallback(() => {
    const token = tokenManager.getToken();
    if (!isAuthenticated || !token || token === 'local-session') {
      setTenant(null);
      setLoading(false);
      return;
    }
    const request = requestRef.current + 1;
    requestRef.current = request;
    setLoading(true);
    authService
      .getActiveTenant()
      .then((response) => {
        if (requestRef.current !== request) return;
        const data = response?.data || response || null;
        setTenant(data?.name ? data : null);
      })
      .catch(() => {
        if (requestRef.current === request) setTenant(null);
      })
      .finally(() => {
        if (requestRef.current === request) setLoading(false);
      });
    // scopeKey is the identity/scope this fetch is for — refetch when it moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, scopeKey]);

  useEffect(() => {
    load();
  }, [load]);

  // Re-read after the tenant's own profile is saved, wherever that happened.
  useEffect(() => {
    window.addEventListener(TENANT_UPDATED_EVENT, load);
    return () => window.removeEventListener(TENANT_UPDATED_EVENT, load);
  }, [load]);

  return { tenant, loading, reload: load };
}

export default useTenant;
